import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Pagination,
  TextField,
  Button,
  IconButton,
  Skeleton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  InputAdornment,
  TableContainer,
  TableHead,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Page from 'src/components/Page';
import palette from 'src/theme/palette';
import { getLeads, getLeadsTypes, deleteLeads } from 'src/ApiServices';
import { toast } from 'react-toastify';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LeadModal from 'src/sections/AddEditLeads';
import LeadViewModal from 'src/sections/LeadViewModal';

export default function LeadManagement() {
  const theme = useTheme();

  // State Management
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  });
  const [leads, setLeads] = useState([]);
  const [leadTypes, setLeadTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch leads data function - FIXED
  const fetchLeadsData = async (currentPage, search = '', type = '') => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 10
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      // FIX: match backend key name (use lead_type_id instead of type)
      if (type) {
        params.lead_type_id = type;
      }

      console.log('API Params:', params);

      const response = await getLeads(params);
      setLeads(response.data || []);

      if (response.pagination) {
        setPagination(response.pagination);
      } else {
        setPagination({
          current_page: currentPage,
          per_page: 10,
          total: response.data?.length || 0,
          last_page: Math.ceil((response.data?.length || 0) / 10) || 1
        });
      }
    } catch (error) {
      console.error('Failed to load leads', error);
      toast.error('Failed to load leads');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch lead types
  const fetchLeadTypes = async () => {
    try {
      const response = await getLeadsTypes();
      setLeadTypes(response.data || []);
    } catch (error) {
      console.error('Failed to load lead types', error);
      toast.error('Failed to load lead types');
    }
  };

  // Search and filter handlers
  const handleSearchSubmit = useCallback(async () => {
    setPage(1);
    await fetchLeadsData(1, searchQuery, selectedType);
  }, [searchQuery, selectedType]);

  const handleClearSearch = useCallback(async () => {
    setSearchQuery('');
    setSelectedType('');
    setPage(1);
    await fetchLeadsData(1, '', '');
  }, []);

  const handleTypeChange = (event) => {
    const newType = event.target.value;
    setSelectedType(newType);
    setPage(1);
    fetchLeadsData(1, searchQuery, newType);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    fetchLeadsData(value, searchQuery, selectedType);
  };

  const handleAddClick = () => {
    setSelectedLead(null);
    setModalOpen(true);
  };

  const handleEditClick = (lead) => {
    setSelectedLead(lead);
    setModalOpen(true);
  };

  const handleViewClick = (lead) => {
    setSelectedLead(lead);
    setViewModalOpen(true);
  };

  const handleDeleteClick = (lead) => {
    setSelectedLead(lead);
    setDeleteModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setViewModalOpen(false);
    setDeleteModalOpen(false);
    setSelectedLead(null);
  };

  const handleModalSuccess = () => {
    handleCloseModal();
    fetchLeadsData(page, searchQuery, selectedType);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedLead(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedLead) return;

    try {
      setDeleteLoading(true);
      await deleteLeads(selectedLead.id);
      toast.success('Lead deleted successfully!');

      if (leads.length === 1 && page > 1) {
        const newPage = page - 1;
        setPage(newPage);
        await fetchLeadsData(newPage, searchQuery, selectedType);
      } else {
        await fetchLeadsData(page, searchQuery, selectedType);
      }

      setDeleteModalOpen(false);
      setSelectedLead(null);
    } catch (error) {
      console.error('Failed to delete lead:', error);
      toast.error(error.response?.data?.message || 'Failed to delete lead!');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Load leads when page, search or filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLeadsData(page, searchQuery, selectedType);
    }, 300);

    return () => clearTimeout(timer);
  }, [page, selectedType]);

  // Load initial data
  useEffect(() => {
    fetchLeadsData(1, '');
    fetchLeadTypes();
  }, []);

  // Table column styles
  const stickyActionsColumnStyle = {
    px: 2,
    position: { xs: 'static', sm: 'sticky' },
    right: 0,
    width: 140,
    textAlign: 'center',
    zIndex: 1,
    backgroundColor: theme.palette.background.paper
  };

  const renderSkeletonRows = () => {
    return Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" />
        </TableCell>
        <TableCell sx={stickyActionsColumnStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
          </Box>
        </TableCell>
      </TableRow>
    ));
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Render property details component
  const renderPropertyDetails = (lead) => {
    if (!lead.property_details) {
      return (
        <Typography
          sx={{
            color: palette.text.secondary,
            // fontStyle: 'italic',
            fontSize: '13px'
          }}
        >
          No Listing Attached
        </Typography>
      );
    }

    const property = lead.property_details;

    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Property Image */}
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 1,
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: palette.grey[200]
          }}
        >
          <img
            src={property.main_image}
            alt={property.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: palette.grey[300]
            }}
          >
            <Typography variant="caption" color="textSecondary">
              No Image
            </Typography>
          </Box>
        </Box>

        {/* Property Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={property.title} arrow>
            <Typography
              sx={{
                fontFamily: 'poppinsMedium',
                fontSize: '14px',
                color: palette.text.primary,
                lineHeight: 1.2,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {truncateText(property.title, 25)}
            </Typography>
          </Tooltip>

          <Tooltip title={property.address} arrow>
            <Typography
              sx={{
                fontSize: '12px',
                color: palette.text.secondary,
                lineHeight: 1.2,
                mb: 0.5,
                mt: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {truncateText(property.address, 30)}
            </Typography>
          </Tooltip>

          <Typography
            sx={{
              fontSize: '11px',
              color: palette.text.disabled,
              lineHeight: 1.2,
              mt: 1.3
            }}
          >
            {property.city || 'N/A'} | {property.state || 'N/A'}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Page title={`Lead Management | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            mb: 3,
            mt: 2,
            pb: 2,
            pl: 1,
            gap: 2
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Lead Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              '&:hover': { background: palette.primary.dark }
            }}
          >
            Add Lead
          </Button>
        </Box>

        {/* Search and Filter Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={9}>
            <TextField
              variant="outlined"
              placeholder="Search leads..."
              size="medium"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Filter by Type</InputLabel>
              <Select value={selectedType} onChange={handleTypeChange} label="Filter by Type">
                <MenuItem value="">All Types</MenuItem>
                {leadTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              variant="contained"
              onClick={handleSearchSubmit}
              fullWidth
              sx={{ height: 56 }}
              startIcon={<SearchIcon />}
            >
              {/* Search */}
            </Button>
          </Grid>
        </Grid>

        {/* Leads Table */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              sx={{
                overflow: 'auto',
                '&::-webkit-scrollbar': { height: 8, width: 8 },
                '&::-webkit-scrollbar-track': { background: palette.grey[200] },
                '&::-webkit-scrollbar-thumb': {
                  background: palette.grey[400],
                  borderRadius: 2
                }
              }}
            >
              <Table
                sx={{
                  minWidth: 1200,
                  '& .MuiTableCell-head': {
                    backgroundColor: theme.palette.background.paper,
                    color: palette.primary.tableHeadTitle,
                    fontFamily: 'poppinsSemiBold',
                    fontSize: '14px',
                    borderBottom: `2px solid ${palette.divider}`,
                    py: 2
                  },
                  '& .MuiTableCell-body': {
                    color: palette.text.primary,
                    fontFamily: 'poppinsRegular',
                    fontSize: '14px',
                    borderBottom: `1px solid ${palette.divider}`,
                    py: 2
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ px: 2 }}>Property Details</TableCell>
                    <TableCell sx={{ px: 2 }}>Name</TableCell>
                    <TableCell sx={{ px: 2 }}>Email</TableCell>
                    <TableCell sx={{ px: 2 }}>Contact</TableCell>
                    <TableCell sx={{ px: 2 }}>Type</TableCell>
                    <TableCell sx={{ px: 2 }}>Notes</TableCell>
                    <TableCell sx={{ px: 2 }}>Date</TableCell>
                    <TableCell sx={stickyActionsColumnStyle}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    renderSkeletonRows()
                  ) : leads.length > 0 ? (
                    leads.map((lead) => (
                      <TableRow
                        key={lead.id}
                        sx={{
                          '&:hover': { backgroundColor: palette.action.hover }
                        }}
                      >
                        <TableCell sx={{ px: 2, minWidth: 200 }}>
                          {renderPropertyDetails(lead)}
                        </TableCell>
                        <TableCell sx={{ px: 2 }}>
                          <Typography>{lead.name || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell sx={{ px: 2 }}>{lead.email || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 2 }}>{lead.contact || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 2 }}>{lead.lead_type?.name || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 2 }}>
                          <Tooltip title={lead.note || 'No notes'} arrow>
                            <Typography
                              sx={{
                                color: palette.text.secondary,
                                fontSize: '13px',
                                maxWidth: 150,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {truncateText(lead.note, 25)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ px: 2 }}>
                          <Typography
                            sx={{
                              color: palette.text.secondary,
                              fontSize: '13px'
                            }}
                          >
                            {lead.date || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={stickyActionsColumnStyle}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title="View Lead">
                              <IconButton
                                size="small"
                                onClick={() => handleViewClick(lead)}
                                sx={{ color: palette.info.main }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Lead">
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(lead)}
                                sx={{ color: palette.primary.main }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Lead">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(lead)}
                                sx={{ color: palette.error.main }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          {searchQuery || selectedType
                            ? 'No leads found matching your filters'
                            : 'No leads found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Pagination */}
        {!loading && pagination.total >= 0 && pagination.last_page >= 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* Add/Edit Modal */}
        <LeadModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSuccess={handleModalSuccess}
          lead={selectedLead}
          leadTypes={leadTypes}
        />

        {/* View Modal */}
        <LeadViewModal open={viewModalOpen} onClose={handleCloseModal} lead={selectedLead} />

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleDeleteCancel}
          PaperProps={{
            sx: {
              borderRadius: 2,
              padding: 1,
              margin: { xs: 2, sm: 3 },
              maxWidth: '400px',
              width: '100%'
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3, pb: 2 }}>
            <WarningAmberIcon
              sx={{
                fontSize: 60,
                color: palette.warning.main,
                mb: 2
              }}
            />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}>
              Are you sure you want to delete lead <strong>"{selectedLead?.name}"</strong>?
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                minWidth: 100,
                '&:hover': {
                  backgroundColor: palette.error.dark
                }
              }}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
